<template>
  <div class="chatpopup-section d-flex h-100">
    <!-- ───────────── 좌측: 날짜/액션 ───────────── -->
    <aside
      class="date-section border-end p-3 d-flex flex-column gap-3"
      style="width: 25rem; max-width: 30%"
    >
      <!-- 1) 닫기 버튼 -->
      <button
        type="button"
        class="btn btn-outline-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
        @click="closeChatPopup"
      >
        <img
          src="@/assets/resources/img/delete-btn-blue.png"
          alt=""
          width="14"
          height="14"
        />
        <span>채팅창 닫기</span>
      </button>

      <!-- 2) 날짜 그룹 -->
      <div class="date-groups flex-grow-1 overflow-auto">
        <div class="list-group list-group-flush">
          <button
            v-for="group in dateGroupList"
            :key="group.key"
            type="button"
            class="list-group-item list-group-item-action text-center"
            :class="{ active: selectedGroupKey === group.key }"
            @click="selectGroup(group.key)"
          >
            {{ group.label }}
          </button>
        </div>
      </div>

      <!-- 3) 대화 내용 지우기 -->
      <button
        type="button"
        class="btn btn-outline-secondary w-100"
        @click="clearChats"
      >
        대화 내용 지우기
      </button>
    </aside>

    <!-- ───────────── 우측: 채팅 ───────────── -->
    <section class="chat-wrapper flex-grow-1 d-flex flex-column">
      <div
        ref="chatSection"
        class="chat-section flex-grow-1 overflow-auto p-3 d-flex flex-column gap-3"
      >
        <!-- 첫 인사 -->
        <div class="d-flex gap-2">
          <div class="mpnite-profile flex-shrink-0 rounded-circle"></div>
          <div
            class="mpnite-chat border border-primary bg-body rounded-3 p-2"
            style="max-width: 68%"
          >
            <p class="mb-2">안녕하세요, 회원님.<br />무엇을 도와드릴까요?</p>
            <p class="operating-hours small text-body-secondary mb-0">
              운영시간: 월-금, 10:00 ~ 18:00
            </p>
          </div>
        </div>

        <!-- 날짜 필터 적용된 채팅 -->
        <template v-for="chat in filteredChats" :key="chat.id">
          <!-- AI/운영자 -->
          <div v-if="!chat.isUser" class="d-flex gap-2">
            <div class="mpnite-profile flex-shrink-0 rounded-circle"></div>
            <div
              class="mpnite-chat border bg-body rounded-3 p-2"
              style="max-width: 68%"
            >
              <p class="mb-1" v-html="sanitizeAndFormat(chat.message)"></p>
              <p class="chat-timestamp small text-body-secondary text-end mb-0">
                {{ formatTime(chat.timestamp) }}
              </p>
            </div>
          </div>

          <!-- 사용자 -->
          <div v-else class="d-flex justify-content-end">
            <div
              class="user-chat bg-primary text-white rounded-3 p-2"
              style="max-width: 68%"
            >
              <p class="mb-1">{{ chat.message }}</p>
              <p class="chat-timestamp small text-white-50 text-end mb-0">
                {{ formatTime(chat.timestamp) }}
              </p>
            </div>
          </div>
        </template>
      </div>

      <!-- 입력 영역 -->
      <div class="chat-input-section bg-body-tertiary p-3">
        <!-- 파일첨부 기능 비활성화
        <input
          type="file"
          ref="fileInputRef"
          class="d-none"
          accept=".pdf,.docx,.txt,.step,.stp,.stl,.jpg,.jpeg,.png"
          @change="handleFileUpload"
        />
        -->
        <div class="input-group">
          <!-- 파일첨부 버튼 비활성화
          <button
            class="btn btn-outline-secondary"
            type="button"
            @click="fileInputRef.click()"
            :disabled="isLoading"
            title="파일 첨부 (PDF, DOCX, TXT, STEP, STL, JPG, PNG)"
          >
            📎
          </button>
          -->
          <input
            type="text"
            class="form-control"
            v-model="userInput"
            :disabled="isLoading"
            placeholder="문의하고 싶으신 내용을 입력하세요"
            @keydown.enter.prevent="handleEnterPress"
          />
          <button
            class="btn btn-primary"
            @click="handleEnterPress"
            :disabled="isLoading || !userInput.trim()"
          >
            <span v-if="!isLoading">전송</span>
            <span v-else class="d-inline-flex align-items-center gap-2">
              <span
                class="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
              전송 중…
            </span>
          </button>
        </div>
        <!-- 파일첨부 기능 비활성화
        <p v-if="uploadingFileName" class="small text-body-secondary mt-1 mb-0">
          📎 {{ uploadingFileName }} 업로드 중…
        </p>
        -->
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, getCurrentInstance } from "vue";
import aichatApi from "@/common/api/aichatApi";
import { logError, logDebug } from "@/utils/logHelper";

const CONV_ID_KEY = "mpnite_conv_id";

const emit = defineEmits(["close"]);

const userInput = ref("");
const chats = ref([]);
const chatId = ref(1);
const isLoading = ref(false);
const selectedGroupKey = ref("today");
const chatSection = ref(null);
const conversationId = ref(null);
// const fileInputRef = ref(null);     // 파일첨부 비활성화
// const uploadingFileName = ref(null); // 파일첨부 비활성화
const { proxy } = getCurrentInstance();

const dateGroupList = [
  { key: "today", label: "오늘" },
  { key: "yesterday", label: "어제" },
  { key: "last7", label: "지난 7일" },
  { key: "last30", label: "지난 30일" },
];

function isSameDate(d1, d2) {
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

const todayChats = computed(() => {
  const t = new Date();
  return chats.value.filter((c) => isSameDate(new Date(c.timestamp), t));
});
const yesterdayChats = computed(() => {
  const y = new Date();
  y.setDate(y.getDate() - 1);
  return chats.value.filter((c) => isSameDate(new Date(c.timestamp), y));
});
const last7DaysChats = computed(() => {
  const s = new Date();
  s.setDate(s.getDate() - 7);
  return chats.value.filter((c) => {
    const d = new Date(c.timestamp);
    return d >= s && !isSameDate(d, new Date());
  });
});
const last30DaysChats = computed(() => {
  const s30 = new Date();
  s30.setDate(s30.getDate() - 30);
  const s7 = new Date();
  s7.setDate(s7.getDate() - 7);
  return chats.value.filter((c) => {
    const d = new Date(c.timestamp);
    return d >= s30 && d < s7;
  });
});

const filteredChats = computed(() => {
  switch (selectedGroupKey.value) {
    case "today": return todayChats.value;
    case "yesterday": return yesterdayChats.value;
    case "last7": return last7DaysChats.value;
    case "last30": return last30DaysChats.value;
    default: return chats.value;
  }
});

onMounted(async () => {
  await initConversation();
  nextTick(scrollToBottom);
});

async function initConversation() {
  try {
    const savedId = localStorage.getItem(CONV_ID_KEY);
    if (savedId) {
      conversationId.value = Number(savedId);
      const history = await aichatApi.aiChatHistory({ conversationId: conversationId.value });
      if (Array.isArray(history)) {
        chats.value = history
          .filter((h) => h.sender !== "AI" || !h.content?.startsWith("안녕하세요 견적"))
          .map((h) => ({
            id: h.id,
            message: h.content,
            isUser: h.sender === "USER",
            timestamp: h.createdAt,
          }));
        chatId.value = chats.value.length ? Math.max(...chats.value.map((c) => c.id)) + 1 : 1;
      }
    } else {
      await createNewConversation();
    }
  } catch (e) {
    logError("[LiveChatPopup] initConversation error:", e);
    await createNewConversation();
  }
}

async function createNewConversation() {
  const conv = await aichatApi.aiChatNew({ general: true });
  conversationId.value = conv.conversationId;
  localStorage.setItem(CONV_ID_KEY, conv.conversationId);
}

function selectGroup(groupKey) {
  selectedGroupKey.value = groupKey;
  nextTick(scrollToBottom);
}
function addChat(message, isUser) {
  const nowIso = new Date().toISOString();
  chats.value.push({ id: chatId.value++, message, isUser, timestamp: nowIso });
  nextTick(scrollToBottom);
}
function handleEnterPress() {
  if (!userInput.value.trim() || isLoading.value) return;
  sendMessage();
}

async function sendMessage() {
  const inputText = userInput.value.trim();
  addChat(inputText, true);
  userInput.value = "";
  isLoading.value = true;

  try {
    logDebug("[LiveChatPopup] sendMessage →", inputText);
    const response = await aichatApi.aiChatEstimate({
      conversationId: conversationId.value,
      content: inputText,
    });
    logDebug("[LiveChatPopup] aiChatEstimate 응답 →", response);

    if (response?.content) {
      addChat(response.content, false);
    } else {
      proxy?.$toast?.error("AI 응답을 받을 수 없습니다.");
      logError("[LiveChatPopup] AI 응답이 비어있습니다.");
    }
  } catch (error) {
    if (error.errorResponse) {
      proxy?.$toast?.error(error.errorResponse.message || "AI 서버 오류가 발생했습니다.");
    } else if (error.response) {
      const serverMsg = error.response.data?.message || `서버 오류 (${error.response.status})`;
      proxy?.$toast?.error(serverMsg);
    } else if (error.request) {
      proxy?.$toast?.error("서버로부터 응답이 없습니다.");
    } else {
      proxy?.$toast?.error("AI 호출 중 예기치 않은 오류가 발생했습니다.");
    }
    logError("[LiveChatPopup] error:", error);
  } finally {
    isLoading.value = false;
    nextTick(scrollToBottom);
  }
}

// 파일첨부 기능 비활성화
// async function handleFileUpload(event) {
//   const file = event.target.files[0];
//   if (!file) return;
//   addChat(`📎 ${file.name}`, true);
//   isLoading.value = true;
//   uploadingFileName.value = file.name;
//   try {
//     const formData = new FormData();
//     formData.append("files[]", file);
//     const result = await aichatApi.aiChatDocs(formData, conversationId.value);
//     const responseText = result && Object.values(result)[0];
//     if (responseText) {
//       addChat(responseText, false);
//     } else {
//       proxy?.$toast?.error("파일 분석 결과를 받을 수 없습니다.");
//     }
//   } catch (error) {
//     const msg =
//       error.code === "E003"
//         ? "지원하지 않는 파일 형식입니다."
//         : error.message || "파일 업로드 중 오류가 발생했습니다.";
//     proxy?.$toast?.error(msg);
//     logError("[LiveChatPopup] file upload error:", error);
//   } finally {
//     isLoading.value = false;
//     uploadingFileName.value = null;
//     if (fileInputRef.value) fileInputRef.value.value = "";
//     nextTick(scrollToBottom);
//   }
// }

function closeChatPopup() {
  emit("close");
}

async function clearChats() {
  try {
    if (conversationId.value) {
      await aichatApi.aiChatDelete({ conversationId: conversationId.value });
    }
  } catch (e) {
    logError("[LiveChatPopup] clearChats delete error:", e);
  }
  chats.value = [];
  chatId.value = 1;
  conversationId.value = null;
  localStorage.removeItem(CONV_ID_KEY);
  try {
    await createNewConversation();
  } catch (e) {
    logError("[LiveChatPopup] clearChats new conversation error:", e);
  }
  proxy?.$toast?.info("대화 내용이 모두 삭제되었습니다.");
  nextTick(scrollToBottom);
}

function sanitizeAndFormat(raw) {
  if (typeof raw !== "string") return "";
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  return escaped.replace(/\r?\n/g, "<br />");
}

function padZero(n) {
  return n < 10 ? `0${n}` : String(n);
}
function formatTime(iso) {
  const d = new Date(iso);
  const now = new Date();
  if (isSameDate(d, now))
    return `${padZero(d.getHours())}:${padZero(d.getMinutes())}`;
  return `${padZero(d.getMonth() + 1)}/${padZero(d.getDate())} ${padZero(d.getHours())}:${padZero(d.getMinutes())}`;
}
function scrollToBottom() {
  if (chatSection.value)
    chatSection.value.scrollTop = chatSection.value.scrollHeight;
}
</script>

<style scoped>
/* 최소 커스텀 — Bootstrap 유틸리티로 대부분 대체 */
.mpnite-profile {
  width: 40px;
  height: 40px;
  background-image: url("@/assets/resources/img/chatprofile-icon-profile.png");
  background-size: cover;
  background-position: center;
}
@media (max-width: 1023.98px) {
  .mpnite-profile {
    width: 36px;
    height: 36px;
  }
}
</style>
